import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Building2, 
  Phone, 
  Mail, 
  CheckCircle2, 
  Star,
  Hammer,
  Award,
  Users,
  Clock,
  Download,
  Calendar,
  ArrowRight,
  MessageSquare,
  X,
  Target,
  Shield,
  TrendingUp,
  Menu,
  AlertCircle
} from 'lucide-react';
import { mockData } from '../data/mock';
import EBookModal from '../components/EBookModal';
import ConsultationModal from '../components/ConsultationModal';
import CostEstimator from '../components/CostEstimator';
import WhatsAppButton from '../components/WhatsAppButton';
import GoogleReviews from '../components/GoogleReviews';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [isEBookOpen, setIsEBookOpen] = useState(false);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');
  const [showEBookPopup, setShowEBookPopup] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Show e-book popup after 30 seconds on desktop (non-intrusive delay for Google Ads compliance)
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      const timer = setTimeout(() => {
        setShowEBookPopup(true);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, contactForm);

      if (response.data.success) {
        setContactSubmitted(true);
        setTimeout(() => {
          setContactSubmitted(false);
          setContactForm({ name: '', email: '', phone: '', message: '' });
        }, 3000);
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setContactError('Failed to send message. Please try again or call us directly.');
    } finally {
      setContactLoading(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-[rgba(0,0,0,0.1)] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="MM Renovations LTD"
                className="h-12 w-auto object-contain brightness-150"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#about" className="text-[rgb(131,146,140)] hover:text-[rgb(0,55,32)] transition-colors font-medium">About</a>
              <a href="#services" className="text-[rgb(131,146,140)] hover:text-[rgb(0,55,32)] transition-colors font-medium">Services</a>
              <a href="#projects" className="text-[rgb(131,146,140)] hover:text-[rgb(0,55,32)] transition-colors font-medium">Projects</a>
              <a href="#process" className="text-[rgb(131,146,140)] hover:text-[rgb(0,55,32)] transition-colors font-medium">Process</a>
              <a href="#testimonials" className="text-[rgb(131,146,140)] hover:text-[rgb(0,55,32)] transition-colors font-medium">Testimonials</a>
              <Button 
                onClick={() => setIsConsultationOpen(true)}
                className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold rounded-full h-10 px-6 hover:scale-102 transition-transform"
              >
                Book Now
              </Button>
            </nav>

            {/* Mobile Menu Button & Book Now */}
            <div className="md:hidden flex items-center gap-3">
              <Button 
                onClick={() => setIsConsultationOpen(true)}
                className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold rounded-full h-10 px-5 text-sm"
              >
                Book Now
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-[rgb(0,55,32)] p-2"
                aria-label="Toggle menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[rgba(0,0,0,0.1)] shadow-lg">
            <nav className="px-4 py-4 space-y-3">
              <a 
                href="#about" 
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[rgb(0,55,32)] font-medium border-b border-[rgba(0,0,0,0.1)]"
              >
                About Us
              </a>
              <a 
                href="#services" 
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[rgb(0,55,32)] font-medium border-b border-[rgba(0,0,0,0.1)]"
              >
                Services
              </a>
              <a 
                href="#projects" 
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[rgb(0,55,32)] font-medium border-b border-[rgba(0,0,0,0.1)]"
              >
                Projects
              </a>
              <a 
                href="#process" 
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[rgb(0,55,32)] font-medium border-b border-[rgba(0,0,0,0.1)]"
              >
                Process
              </a>
              <a 
                href="#testimonials" 
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[rgb(0,55,32)] font-medium border-b border-[rgba(0,0,0,0.1)]"
              >
                Testimonials
              </a>
              <a 
                href="#contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-[rgb(0,55,32)] font-medium"
              >
                Contact
              </a>
              <div className="pt-2">
                <a 
                  href={`tel:${mockData.company.phone}`}
                  className="flex items-center gap-2 py-3 text-[var(--accent-primary)] font-semibold"
                >
                  <Phone className="w-5 h-5" />
                  {mockData.company.phone}
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section with Video Background */}
      <section className="min-h-screen flex items-center justify-center pt-24 pb-12 px-4 sm:px-6 relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://assets.mixkit.co/videos/preview/mixkit-construction-worker-working-on-a-building-4484-large.mp4" type="video/mp4" />
          </video>
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-white"></div>
        </div>
        
        {/* Content */}
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="text-5xl sm:text-5xl md:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6 drop-shadow-lg gradient-text animate-fade-in" style={{ letterSpacing: '-0.02em', WebkitTextFillColor: 'white', backgroundClip: 'border-box' }}>
            Transform Your Space,<br />Elevate Your Life
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed drop-shadow-md px-4">
            Specializing in full home renovations and SDU creations. Expert craftsmanship.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={() => setIsEBookOpen(true)}
              className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold text-lg h-14 px-8 rounded-full hover:scale-110 transition-all shadow-lg animate-pulse-glow btn-press"
            >
              <Download className="w-5 h-5 mr-2 animate-bounce-gentle" />
              Free Renovation Guide
            </Button>
            <Button 
              onClick={() => setIsConsultationOpen(true)}
              variant="outline"
              className="text-lg h-14 px-8 rounded-full border-2 border-white/80 text-white hover:bg-white hover:text-[var(--accent-primary)] transition-all font-semibold backdrop-blur-sm hover:scale-105 btn-press"
            >
              <Calendar className="w-5 h-5 mr-2 icon-bounce" />
              Book Free Consultation
            </Button>
          </div>
          <div className="mt-12 flex flex-col sm:flex-row gap-8 justify-center items-center text-white/80">
            <div className="flex items-center gap-2 drop-shadow">
              <Phone className="w-5 h-5 text-white" />
              <a href={`tel:${mockData.company.phone}`} className="hover:text-white transition-colors">
                {mockData.company.phone}
              </a>
            </div>
            <div className="flex items-center gap-2 drop-shadow">
              <Mail className="w-5 h-5 text-white" />
              <a href={`mailto:${mockData.company.email}`} className="hover:text-white transition-colors">
                {mockData.company.email}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Main Heading */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-[rgb(0,55,32)] mb-4">
              {mockData.about.mainTitle}
            </h2>
            <p className="text-xl text-[rgb(131,146,140)] max-w-3xl mx-auto">
              {mockData.about.subtitle}
            </p>
          </div>

          {/* Story Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-[rgba(45,80,22,0.05)] to-white border-[rgba(0,0,0,0.1)] rounded-2xl">
              <p className="text-lg md:text-xl text-[rgb(14,15,12)] leading-relaxed mb-6">
                {mockData.about.story}
              </p>
              <div className="flex items-start gap-3 p-6 bg-white rounded-xl border-l-4 border-[var(--accent-primary)]">
                <Target className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-[rgb(0,55,32)] mb-2 text-lg">Our Mission</h3>
                  <p className="text-[rgb(131,146,140)] leading-relaxed">
                    {mockData.about.mission}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            {mockData.about.stats.map((stat, index) => (
              <Card 
                key={index}
                className="p-6 text-center bg-white border-[rgba(0,0,0,0.1)] rounded-xl hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] rounded-full flex items-center justify-center mx-auto mb-4">
                  {stat.icon === 'check' && <CheckCircle2 className="w-6 h-6 text-white" />}
                  {stat.icon === 'award' && <Award className="w-6 h-6 text-white" />}
                  {stat.icon === 'star' && <Star className="w-6 h-6 text-white fill-white" />}
                  {stat.icon === 'dollar' && <TrendingUp className="w-6 h-6 text-white" />}
                </div>
                <div className="text-3xl md:text-4xl font-bold text-[rgb(0,55,32)] mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-[rgb(131,146,140)] font-medium">
                  {stat.label}
                </div>
              </Card>
            ))}
          </div>

          {/* Values Grid */}
          <div className="mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-[rgb(0,55,32)] text-center mb-12">
              What Sets Us Apart
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {mockData.about.values.map((value, index) => (
                <Card 
                  key={index}
                  className="p-8 bg-white border-[rgba(0,0,0,0.1)] rounded-xl hover:shadow-xl transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] rounded-xl flex items-center justify-center">
                      {value.icon === 'hammer' && <Hammer className="w-7 h-7 text-white" />}
                      {value.icon === 'message' && <MessageSquare className="w-7 h-7 text-white" />}
                      {value.icon === 'clock' && <Clock className="w-7 h-7 text-white" />}
                      {value.icon === 'shield' && <Shield className="w-7 h-7 text-white" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[rgb(0,55,32)] mb-3">
                        {value.title}
                      </h4>
                      <p className="text-[rgb(131,146,140)] leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <Card className="p-8 md:p-12 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white rounded-2xl">
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Why Choose MM Renovations?
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {mockData.about.whyChooseUs.map((reason, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <span className="text-lg">{reason}</span>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Button 
                onClick={() => setIsConsultationOpen(true)}
                className="bg-white text-[var(--accent-primary)] font-semibold text-lg h-14 px-10 rounded-full hover:scale-102 transition-transform shadow-lg hover:shadow-xl"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Your Free Consultation
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-white confetti-bg">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4">Our Services</h2>
            <p className="text-xl text-[rgb(131,146,140)] max-w-2xl mx-auto">
              From concept to completion, we deliver exceptional results
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {mockData.services.map((service) => (
              <Card 
                key={service.id}
                className="p-8 bg-white border-[rgba(0,0,0,0.1)] rounded-xl card-hover cursor-pointer"
              >
                <h3 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">{service.title}</h3>
                <p className="text-[rgb(131,146,140)] mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-[rgb(14,15,12)]">
                      <CheckCircle2 className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => setIsConsultationOpen(true)}
                  className="w-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold h-12 rounded-full hover:scale-105 transition-all btn-shimmer btn-press"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* E-Book CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[rgba(148,242,127,0.1)] to-[rgba(129,221,103,0.1)]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white p-10 rounded-2xl shadow-xl border border-[var(--accent-primary)]">
            <Download className="w-16 h-16 text-[var(--accent-primary)] mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-[rgb(0,55,32)] mb-4">
              {mockData.ebook.title}
            </h2>
            <p className="text-lg text-[rgb(131,146,140)] mb-6 max-w-2xl mx-auto">
              {mockData.ebook.description}
            </p>
            <Button 
              onClick={() => setIsEBookOpen(true)}
              className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold text-lg h-14 px-10 rounded-full hover:scale-102 transition-transform shadow-lg"
            >
              Download Free E-Book Now
            </Button>
          </div>
        </div>
      </section>

      {/* Projects Gallery */}
      <section id="projects" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4">Featured Projects</h2>
            <p className="text-xl text-[rgb(131,146,140)] max-w-2xl mx-auto">
              See the quality of our work and attention to detail
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.projects.map((project) => (
              <Card 
                key={project.id}
                onClick={() => window.location.href = `/project/${project.id}`}
                className="overflow-hidden bg-white border-[rgba(0,0,0,0.1)] rounded-xl card-hover cursor-pointer group"
              >
                <div className="aspect-video bg-[rgba(0,0,0,0.05)] relative overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    style={{
                      imageRendering: 'crisp-edges',
                      WebkitImageRendering: 'crisp-edges'
                    }}
                    loading="lazy"
                  />
                  <div className="absolute top-3 right-3 bg-[var(--accent-primary)] text-white px-3 py-1 rounded-full text-sm font-semibold animate-float">
                    {project.duration}
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-sm text-[rgb(13,121,22)] font-semibold mb-2">{project.category}</div>
                  <h3 className="text-xl font-bold text-[rgb(0,55,32)] mb-2">{project.title}</h3>
                  <p className="text-[rgb(131,146,140)] text-sm">{project.location}</p>
                </div>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button 
              onClick={() => setIsConsultationOpen(true)}
              className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold text-lg h-14 px-10 rounded-full hover:scale-102 transition-transform"
            >
              Start Your Project
            </Button>
          </div>
        </div>
      </section>

      {/* Cost Estimator */}
      <CostEstimator onOpenConsultation={() => setIsConsultationOpen(true)} />

      {/* Process Section */}
      <section id="process" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4">Our Process</h2>
            <p className="text-xl text-[rgb(131,146,140)] max-w-2xl mx-auto">
              A seamless journey from consultation to completion
            </p>
          </div>
          <div className="space-y-6">
            {mockData.process.map((step, index) => (
              <div 
                key={step.step}
                className="flex flex-col md:flex-row gap-6 items-start p-6 bg-white border border-[rgba(0,0,0,0.1)] rounded-xl hover:shadow-lg transition-all"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {step.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-[rgb(0,55,32)] mb-2">{step.title}</h3>
                  <p className="text-[rgb(131,146,140)] mb-2 leading-relaxed">{step.description}</p>
                  <div className="flex items-center gap-2 text-sm text-[rgb(13,121,22)] font-semibold">
                    <Clock className="w-4 h-4" />
                    {step.duration}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button 
              onClick={() => setIsConsultationOpen(true)}
              className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold text-lg h-14 px-10 rounded-full hover:scale-102 transition-transform"
            >
              Let's Get Started
            </Button>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews />

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-[rgb(131,146,140)]">
              Everything you need to know about working with us
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {mockData.faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`}
                className="bg-white border border-[rgba(0,0,0,0.1)] rounded-xl px-6"
              >
                <AccordionTrigger className="text-left font-semibold text-[rgb(0,55,32)] hover:text-[rgb(13,121,22)] py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[rgb(131,146,140)] leading-relaxed pb-5">
                  {faq.answer}
                  {faq.link && (
                    <a
                      href={faq.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-3 text-[rgb(13,121,22)] font-semibold hover:underline"
                      data-testid={`faq-link-${faq.id}`}
                    >
                      {faq.link.text} &rarr;
                    </a>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-12">
            <p className="text-[rgb(131,146,140)] mb-4">Still have questions?</p>
            <Button 
              onClick={scrollToContact}
              variant="outline"
              className="h-12 px-8 rounded-full border-[rgba(0,0,0,0.2)] hover:bg-[rgba(0,0,0,0.05)] font-semibold"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-6 bg-[rgba(0,0,0,0.02)]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4">Meet Our Team</h2>
            <p className="text-xl text-[rgb(131,146,140)] max-w-2xl mx-auto">
              Experienced professionals dedicated to your project
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.team.map((member) => (
              <Card 
                key={member.id}
                className="p-8 text-center bg-white border-[rgba(0,0,0,0.1)] rounded-xl"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Users className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[rgb(0,55,32)] mb-2">{member.name}</h3>
                <div className="text-[rgb(13,121,22)] font-semibold mb-3">{member.role}</div>
                <p className="text-[rgb(131,146,140)] text-sm">{member.bio}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4">Get In Touch</h2>
            <p className="text-xl text-[rgb(131,146,140)] max-w-2xl mx-auto">
              Ready to start your renovation? Contact us today
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-[rgb(0,55,32)] mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Phone className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-[rgb(0,55,32)] mb-1">Phone</div>
                    <a href={`tel:${mockData.company.phone}`} className="text-[rgb(131,146,140)] hover:text-[rgb(13,121,22)] transition-colors">
                      {mockData.company.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-[rgb(0,55,32)] mb-1">Email</div>
                    <a href={`mailto:${mockData.company.email}`} className="text-[rgb(131,146,140)] hover:text-[rgb(13,121,22)] transition-colors">
                      {mockData.company.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Building2 className="w-6 h-6 text-[var(--accent-primary)] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold text-[rgb(0,55,32)] mb-1">Service Area</div>
                    <p className="text-[rgb(131,146,140)]">
                      Ottawa & Surrounding Areas
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Button 
                  onClick={() => setIsConsultationOpen(true)}
                  className="w-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold h-12 rounded-full hover:scale-102 transition-transform"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Free Consultation
                </Button>
              </div>
            </div>
            <div>
              <Card className="p-8 bg-white border-[rgba(0,0,0,0.1)] rounded-xl">
                {!contactSubmitted ? (
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="contact-name" className="text-[rgb(0,55,32)]">Name *</Label>
                      <Input
                        id="contact-name"
                        type="text"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        placeholder="Your name"
                        required
                        disabled={contactLoading}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email" className="text-[rgb(0,55,32)]">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        placeholder="your@email.com"
                        required
                        disabled={contactLoading}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-phone" className="text-[rgb(0,55,32)]">Phone</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        placeholder="613-XXX-XXXX"
                        disabled={contactLoading}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-message" className="text-[rgb(0,55,32)]">Message *</Label>
                      <Textarea
                        id="contact-message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        placeholder="Tell us about your project..."
                        rows={4}
                        required
                        disabled={contactLoading}
                        className="mt-1"
                      />
                    </div>
                    {contactError && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{contactError}</span>
                      </div>
                    )}
                    <Button 
                      type="submit"
                      disabled={contactLoading}
                      className="w-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold h-12 rounded-full hover:scale-102 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {contactLoading ? (
                        <>Sending...</>
                      ) : (
                        <>
                          <MessageSquare className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-16 h-16 text-[var(--accent-primary)] mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-[rgb(0,55,32)] mb-2">Message Sent!</h3>
                    <p className="text-[rgb(131,146,140)]">We'll get back to you soon.</p>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[rgb(0,55,32)] text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/logo.png" 
                alt="MM Renovations LTD"
                className="h-16 w-auto object-contain brightness-150 mb-4"
              />
              <p className="text-white/70 text-sm">
                Transform your space with expert craftsmanship and attention to detail.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><a href="#services" className="hover:text-[var(--accent-primary)] transition-colors">Full Home Renovations</a></li>
                <li><a href="#services" className="hover:text-[var(--accent-primary)] transition-colors">SDU Creation</a></li>
                <li><a href="#services" className="hover:text-[var(--accent-primary)] transition-colors">Kitchen Remodeling</a></li>
                <li><a href="#services" className="hover:text-[var(--accent-primary)] transition-colors">Bathroom Renovations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><a href="#projects" className="hover:text-[var(--accent-primary)] transition-colors">Projects</a></li>
                <li><a href="#process" className="hover:text-[var(--accent-primary)] transition-colors">Our Process</a></li>
                <li><a href="#testimonials" className="hover:text-[var(--accent-primary)] transition-colors">Testimonials</a></li>
                <li><a href="#contact" className="hover:text-[var(--accent-primary)] transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>{mockData.company.phone}</li>
                <li>{mockData.company.email}</li>
                <li>Ottawa & Surrounding Areas</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/60">
            <p>&copy; 2026 {mockData.company.name}. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="/privacy-policy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
              <span>|</span>
              <a href="/terms-of-service" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <EBookModal isOpen={isEBookOpen} onClose={() => setIsEBookOpen(false)} />
      <ConsultationModal isOpen={isConsultationOpen} onClose={() => setIsConsultationOpen(false)} />

      {/* WhatsApp Button */}
      <WhatsAppButton />

      {/* Bottom-Left E-Book Popup - Desktop Only */}
      {showEBookPopup && !isEBookOpen && (
        <div className="hidden lg:block fixed bottom-6 left-6 z-50 animate-slide-up">
          <div className="bg-white rounded-2xl shadow-2xl border border-[var(--accent-primary)] p-6 max-w-sm relative">
            <button
              onClick={() => setShowEBookPopup(false)}
              className="absolute top-3 right-3 text-[rgb(175,183,180)] hover:text-[rgb(0,55,32)] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] rounded-full flex items-center justify-center">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[rgb(0,55,32)] mb-1">
                  Free Renovation Guide
                </h3>
                <p className="text-sm text-[rgb(131,146,140)]">
                  Get insider tips & cost-saving strategies
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                setShowEBookPopup(false);
                setIsEBookOpen(true);
              }}
              className="w-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold h-11 rounded-full hover:scale-102 transition-transform"
            >
              Download Now
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
